// Tenant Portal - Everything in one place!
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  Paper,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  Home,
  PaymentRounded,
  Build,
  Description,
  Message,
  CheckCircle,
  Warning,
  PersonOutline
} from "@mui/icons-material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

function TenantPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    verifyAndLoadPortal();
  }, [token]);

  const verifyAndLoadPortal = async () => {
    try {
      // Look up magic link token in Firestore
      const linkDoc = await getDoc(doc(db, "magicLinks", token));
      if (linkDoc.exists() && linkDoc.data().valid !== false) {
        const linkData = linkDoc.data();
        setTenantInfo(linkData);

        // Load tenant portal data
        const tenantDoc = await getDoc(doc(db, "tenants", linkData.tenantId));
        if (tenantDoc.exists()) {
          setPortalData({ id: tenantDoc.id, ...tenantDoc.data() });
        }
      } else {
        setError("Invalid or expired link");
      }
    } catch (error) {
      console.error("Error loading portal:", error);
      setError("Failed to load portal");
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Pay Rent",
      icon: <PaymentRounded sx={{ fontSize: 32 }} />,
      color: "#4CAF50",
      description: "Make a payment",
      action: () => setActiveSection("payments")
    },
    {
      title: "Maintenance",
      icon: <Build sx={{ fontSize: 32 }} />,
      color: "#2196F3",
      description: "Request repairs",
      action: () => setActiveSection("maintenance")
    },
    {
      title: "Documents",
      icon: <Description sx={{ fontSize: 32 }} />,
      color: "#FF9800",
      description: "View lease & docs",
      action: () => setActiveSection("documents")
    },
    {
      title: "Message",
      icon: <Message sx={{ fontSize: 32 }} />,
      color: "#9C27B0",
      description: "Contact landlord",
      action: () => setActiveSection("messages")
    }
  ];

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography>Loading your portal...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3
        }}
      >
        <Card sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
          <Warning sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Oops!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50", py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 2 }}>
        
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
              <PersonOutline sx={{ fontSize: 32 }} />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                Welcome, {tenantInfo?.tenant_name}! 👋
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Everything you need is right here
              </Typography>
            </Box>
            <Chip 
              icon={<CheckCircle />}
              label="Account Active"
              color="success"
              variant="outlined"
            />
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Paper
                      onClick={action.action}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4
                        }
                      }}
                    >
                      <Box sx={{ color: action.color, mb: 1 }}>
                        {action.icon}
                      </Box>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {action.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Card>

            {/* Property Info */}
            {portalData && (
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Your Property
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Home sx={{ color: "primary.main" }} />
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {portalData.property?.address || "Property Address"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monthly Rent: ${portalData.property?.rent || 0}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Payments Made
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {portalData.quick_stats?.payments_made || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      On-Time Rate
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {portalData.quick_stats?.on_time_rate || "100%"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Open Requests
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {portalData.quick_stats?.open_requests || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            )}
          </Grid>

          {/* Balance & Status */}
          <Grid item xs={12} md={4}>
            {portalData && (
              <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Account Balance
                </Typography>
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography 
                    variant="h3" 
                    fontWeight="bold"
                    color={portalData.balance?.current <= 0 ? "success.main" : "error.main"}
                  >
                    ${Math.abs(portalData.balance?.current || 0)}
                  </Typography>
                  <Chip 
                    label={portalData.balance?.status || "Paid"}
                    color={portalData.balance?.current <= 0 ? "success" : "error"}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Next rent due: {portalData.property?.next_due || "1st of next month"}
                  </Typography>
                </Box>
              </Card>
            )}

            {/* Contact Info */}
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Your Info
              </Typography>
              {portalData && (
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Email"
                      secondary={portalData.tenant?.email}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Phone"
                      secondary={portalData.tenant?.phone || "Not provided"}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Move-in Date"
                      secondary={portalData.tenant?.move_in_date || "Not available"}
                    />
                  </ListItem>
                </List>
              )}
              <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                Update Info
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default TenantPortal;